import { Alert, Stack, TextField } from '@mui/material';
import { useState } from 'react';

import type { AvailableLanguage } from '../../api/business/types';
import { LanguageTabs } from '../../components/LanguageTabs';
import { languageName } from '../../components/localisedText';
import {
  emptyTexts,
  META_DESCRIPTION_ADVISED,
  META_DESCRIPTION_MAX,
  META_TITLE_ADVISED,
  META_TITLE_MAX,
} from '../../components/productDraft';
import type { DraftTextSetter, ProductDraft } from '../../components/productDraft';
import { lengthState, trimmedLength } from '../../components/productListing';
import { useLanguage } from '../../i18n/LanguageContext';

type ProductSeoFieldsProps = {
  draft: ProductDraft;
  setText: DraftTextSetter;
  languages: Array<string>;
  available: Array<AvailableLanguage>;
};

/**
 * What search engines show of the product, per content language (R0030 WP-07): a title and a description, each
 * counted as it is typed — a warning past what a results page shows (60 and 155), refused by the API past 70 and
 * 160. Left empty, the shops use the name and the short description; the form says so.
 */
export const ProductSeoFields = ({ draft, setText, languages, available }: ProductSeoFieldsProps) => {
  const { language, t } = useLanguage();
  const [active, setActive] = useState(languages[0] ?? 'sk');
  const current = languages.includes(active) ? active : languages[0];
  const texts = draft.texts[current] ?? emptyTexts();
  const suffix = languages.length > 1 ? ` (${languageName(current, available, language)})` : '';
  const counted = (count: number, advised: number, max: number): { text: string; long: boolean } => {
    const state = lengthState(count, advised, max);

    return {
      text: state === 'OK' ? t('seo.count', { count, max }) : t('seo.countLong', { count, max, advised }),
      long: state !== 'OK',
    };
  };
  const title = counted(trimmedLength(texts.metaTitle), META_TITLE_ADVISED, META_TITLE_MAX);
  const description = counted(
    trimmedLength(texts.metaDescription),
    META_DESCRIPTION_ADVISED,
    META_DESCRIPTION_MAX,
  );

  return (
    <Stack spacing={2}>
      <Alert severity="info" variant="outlined">
        {t('seo.titleFallback')} {t('seo.descriptionFallback')}
      </Alert>
      {languages.length > 1 ? (
        <LanguageTabs
          languages={languages}
          value={current}
          onChange={setActive}
          missing={(code) => (draft.texts[code]?.metaTitle ?? '').trim() === ''}
        />
      ) : null}
      <TextField
        fullWidth
        label={`${t('seo.metaTitle')}${suffix}`}
        placeholder={texts.name}
        helperText={title.text}
        color={title.long ? 'warning' : undefined}
        focused={title.long ? true : undefined}
        FormHelperTextProps={{ sx: title.long ? { color: 'warning.main' } : undefined }}
        value={texts.metaTitle}
        inputProps={{ maxLength: META_TITLE_MAX }}
        onChange={(e) => setText(current, 'metaTitle', e.target.value)}
      />
      <TextField
        fullWidth
        multiline
        minRows={2}
        label={`${t('seo.metaDescription')}${suffix}`}
        helperText={description.text}
        color={description.long ? 'warning' : undefined}
        focused={description.long ? true : undefined}
        FormHelperTextProps={{ sx: description.long ? { color: 'warning.main' } : undefined }}
        value={texts.metaDescription}
        inputProps={{ maxLength: META_DESCRIPTION_MAX }}
        onChange={(e) => setText(current, 'metaDescription', e.target.value)}
      />
    </Stack>
  );
};
